/**
 * Collaborator chat, as a Y.Array on the timeline document.
 *
 * Riding the existing document means chat needs no new transport, endpoint or
 * storage: it syncs, merges and survives a service restart exactly as the
 * timeline does. It stays clear of the edit because everything that reads the
 * edit names its roots explicitly — the snapshot writer
 * (`backend/sync/ydoc.py:doc_to_edit`) reads only meta/sources/tracks, and the
 * UndoManager scopes to those same three, so chat is neither persisted into
 * `Project.timeline` nor undoable.
 *
 * Messages are rendered in **array order**, not sorted by timestamp: array order
 * is what the CRDT converges on, whereas sorting by wall-clock `at` would let
 * one participant's clock skew reorder the conversation for everyone.
 */
import * as Y from 'yjs';
import { genId } from '../format/helpers';
import { CHAT_ORIGIN, ROOT_CHAT } from './schema';

export type ChatKind = 'user' | 'system' | 'agent';

/** A candidate clip the assistant found. Times are CONCAT seconds. */
export interface ClipCard {
  archive: string;
  item: string;
  title: string;
  in: number;
  out: number;
  /** the assistant's one-line reason for picking it */
  note?: string;
}

/**
 * Extra payload rendered below a message. `proposal` carries only an id: the
 * record itself lives in the `agent` root because its status changes and chat
 * entries are append-only (see crdt/agent.ts).
 */
export type Attachment =
  /** The assistant's own pick: a handful of clips it is recommending. */
  | { type: 'clips'; clips: ClipCard[] }
  /**
   * A whole moment search, shown in full. Distinct from `clips` because the
   * intent is different — this is "everything that matched", so it carries the
   * query that produced it and how many matched, and the UI groups and
   * collapses rather than laying every card out in a row.
   *
   * `more` means the archive had further matches beyond `total`. There is no
   * exact grand total: pan.do/ra will not count annotations cheaply (see
   * api/pandora.ts), so the search over-fetches one row to learn whether it hit
   * a ceiling. `total` is therefore exact for an ordinary search and a floor
   * for a very broad one — which is why the header has to say which it is.
   */
  | { type: 'moments'; label: string; total: number; more?: boolean; clips: ClipCard[] }
  | { type: 'proposal'; proposalId: string };

export interface ChatMessage {
  id: string;
  /** `agent` is reserved for the AI assistant (Part 3); present from day one so
   *  it needs no migration when it lands. */
  kind: ChatKind;
  author: { name: string; color: string };
  body: string;
  /** Epoch ms, for display only — never for ordering. */
  at: number;
  /** Which assistant run produced this message, for grouping in the UI. */
  runId?: string;
  atts?: Attachment[];
}

export const MAX_MESSAGE_LENGTH = 2000;
/**
 * Assistant messages get a larger cap: they summarise searches and edits, and
 * truncating one mid-sentence reads as a bug rather than a limit.
 */
export const MAX_AGENT_MESSAGE_LENGTH = 8000;
/** Cards in a `clips` attachment, matching the show_clips tool's own limit. */
const MAX_CLIP_CARDS = 8;

/**
 * Cards in a `moments` attachment.
 *
 * This is a storage budget, not a display one: attachments live in the Y.Doc
 * chat log for the life of the project, and 200 cards is ~24KB per search. The
 * attachment's `total` still reports the true match count, so a wider search
 * reads as "showing 200 of 412" rather than silently looking complete.
 */
const MAX_MOMENT_CARDS = 200;

function chatArray(doc: Y.Doc): Y.Array<unknown> {
  return doc.getArray<unknown>(ROOT_CHAT);
}

const KINDS: ReadonlySet<string> = new Set<ChatKind>(['user', 'system', 'agent']);

/**
 * Is this entry actually a message?
 *
 * Everything in the chat array arrives from a peer, and anyone holding the
 * collaboration link can write arbitrary JSON into the document. A single
 * malformed entry rendered unguarded takes the panel down for *everyone* —
 * and because it is CRDT state, it stays there permanently. So the boundary
 * between wire data and typed data is here, and `readChat` drops anything that
 * does not survive it rather than asserting a shape it cannot guarantee.
 */
function isClipCard(value: unknown): value is ClipCard {
  if (typeof value !== 'object' || value === null) return false;
  const c = value as Record<string, unknown>;
  return (
    typeof c.archive === 'string' &&
    typeof c.item === 'string' &&
    typeof c.title === 'string' &&
    typeof c.in === 'number' &&
    Number.isFinite(c.in) &&
    typeof c.out === 'number' &&
    Number.isFinite(c.out) &&
    (c.note === undefined || typeof c.note === 'string')
  );
}

function isAttachment(value: unknown): value is Attachment {
  if (typeof value !== 'object' || value === null) return false;
  const a = value as Record<string, unknown>;
  if (a.type === 'clips') {
    return Array.isArray(a.clips) && a.clips.length > 0 && a.clips.every(isClipCard);
  }
  if (a.type === 'moments') {
    return (
      typeof a.label === 'string' &&
      typeof a.total === 'number' &&
      Number.isFinite(a.total) &&
      (a.more === undefined || typeof a.more === 'boolean') &&
      Array.isArray(a.clips) &&
      a.clips.length > 0 &&
      a.clips.every(isClipCard)
    );
  }
  if (a.type === 'proposal') return typeof a.proposalId === 'string';
  return false;
}

/**
 * Cap the cards inside an attachment.
 *
 * This used to read `atts.slice(0, MAX_CLIP_CARDS)` at the call site, which
 * capped the number of *attachments* on a message — a limit nothing has ever
 * come close to — while the cards themselves went in unbounded.
 */
function capCards(att: Attachment): Attachment {
  if (att.type === 'clips' && att.clips.length > MAX_CLIP_CARDS) {
    return { ...att, clips: att.clips.slice(0, MAX_CLIP_CARDS) };
  }
  if (att.type === 'moments' && att.clips.length > MAX_MOMENT_CARDS) {
    return { ...att, clips: att.clips.slice(0, MAX_MOMENT_CARDS) };
  }
  return att;
}

/**
 * Keep the message, drop bad attachments. A malformed attachment is a rendering
 * problem; losing the assistant's text along with it would turn a cosmetic
 * failure into a conversation with holes in it.
 */
function sanitizeAtts(value: unknown): Attachment[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const kept = value.filter(isAttachment).map(capCards);
  return kept.length ? kept : undefined;
}

function isChatMessage(value: unknown): value is ChatMessage {
  if (typeof value !== 'object' || value === null) return false;
  const m = value as Record<string, unknown>;
  const author = m.author as Record<string, unknown> | undefined;
  return (
    typeof m.id === 'string' &&
    typeof m.body === 'string' &&
    typeof m.at === 'number' &&
    Number.isFinite(m.at) &&
    typeof m.kind === 'string' &&
    KINDS.has(m.kind) &&
    typeof author === 'object' &&
    author !== null &&
    typeof author.name === 'string' &&
    typeof author.color === 'string'
  );
}

export function readChat(doc: Y.Doc): ChatMessage[] {
  return chatArray(doc)
    .toArray()
    .filter(isChatMessage)
    .map((m) => {
      // Attachments are validated on READ, not in isChatMessage: a message with
      // one bad card should still appear, minus the card.
      const atts = sanitizeAtts((m as ChatMessage).atts);
      return atts ? { ...m, atts } : stripAtts(m);
    });
}

function stripAtts(message: ChatMessage): ChatMessage {
  if (message.atts === undefined) return message;
  const { atts: _dropped, ...rest } = message;
  return rest;
}

/**
 * Append a message. Uses its own origin so it is never captured by the
 * UndoManager and is trivially distinguishable from an edit.
 */
export function postMessage(
  doc: Y.Doc,
  message: Omit<ChatMessage, 'id' | 'at'> & Partial<Pick<ChatMessage, 'id' | 'at'>>,
): void {
  const body = message.body.trim();
  // An empty body is only meaningful when something else carries the content —
  // a clip strip or a proposal card with no preamble is a real message.
  const hasAtts = Array.isArray(message.atts) && message.atts.length > 0;
  if (!body && !hasAtts) return;
  const cap = message.kind === 'agent' ? MAX_AGENT_MESSAGE_LENGTH : MAX_MESSAGE_LENGTH;
  const full: ChatMessage = {
    id: message.id ?? genId('msg'),
    kind: message.kind,
    author: message.author,
    body: body.slice(0, cap),
    at: message.at ?? Date.now(),
  };
  if (message.runId) full.runId = message.runId;
  const atts = sanitizeAtts(message.atts);
  if (atts) full.atts = atts;
  doc.transact(() => {
    chatArray(doc).push([full]);
  }, CHAT_ORIGIN);
}

/**
 * Subscribe to chat changes. Returns an unsubscribe function.
 *
 * Goes through `readChat` rather than the raw array so remote messages get the
 * same validation as the initial read — this is the path a malformed message
 * from a peer actually arrives on.
 */
export function observeChat(doc: Y.Doc, onChange: (messages: ChatMessage[]) => void): () => void {
  const arr = chatArray(doc);
  const handler = () => onChange(readChat(doc));
  arr.observe(handler);
  return () => arr.unobserve(handler);
}
